import { BrandTokens } from "@tento-ui/brand/tokens/index";
import { Brand } from "@tento-ui/brand/brand";
import type {
  Preset,
  RendererOverrides,
  SectionSet,
  TokenSet,
} from "@tento-ui/brand/page-engine.types";
import type { ThemeRef } from "@tento-ui/brand/page-model";
import type { Style } from "@tento-ui/component-styles";
import { Flags } from "@tento-ui/flags";
import { MarketingHeader } from "@tento-ui/brand/header/marketing-header";
import { MarketingMobileMenu } from "@tento-ui/brand/header/marketing-mobile-menu";
import { NavCenteredPopoverRow } from "@tento-ui/brand/blog/nav-centered-popover-row";
import { Breadcrumb } from "@tento-ui/components/breadcrumb/breadcrumb";
import { LandingFooter } from "@tento-ui/brand/landing/landing-footer";

import { MarketingFooter } from "../marketing/lona-marketing-footer";

import { BlogChromeImpl } from "../blog/variants/blog-chrome";
import { BlogBearHeader } from "../blog/blog-bear-header";
import { BlogBearFooter } from "../blog/blog-bear-footer";
import { NavLinksRow } from "../blog/nav-links-row";
import { InstanceUrl } from "@tento-core/instance-url";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

import { claudeRoute } from "../blog/variants/claude-route";
import { listRoute } from "../blog/variants/list-route";
import { bearRoute } from "../blog/variants/bear-route";
import { makeMinimalRoute } from "../blog/variants/minimal-route";
import { editorialPostRoute } from "../blog/variants/editorial-post-route";
import { newsroomPostRoute } from "../blog/variants/newsroom-post-route";
import { bearPostRoute } from "../blog/variants/bear-post-route";
import { minimalPostRoute } from "../blog/variants/minimal-post-route";

import type { BlogIndexRoute, BlogPostRoute } from "../blog/blog-variant";
import {
  claudeContent,
  listContent,
  bearContent,
  minimalContent,
} from "./blog-content";
import { defaultSections, landingPageStyle, makePageFactory } from "./landing-content";
import { tshSectionSet } from "./tsh/index";
import { tshTokens } from "./tsh/tokens";
import {
  landingComparisonRenderer,
  landingCtaRenderer,
  landingFaqRenderer,
  landingFeatureGridRenderer,
  landingHeroRenderer,
  landingIntegrationsRenderer,
  landingPricingRenderer,
  landingSocialProofRenderer,
} from "./landing-page-engine";
import type { PageEngineNodeProps } from "./to-props";
import { postBodyRenderer, postListRenderer } from "./to-props";
import type {
  PageEnginePresetConfig,
  PageEnginePresetName,
} from "./page-engine-presets";
import { resolvePageEnginePresetName } from "./page-engine-presets";
import { stickerSheetRenderer } from "./sticker-section";

// ─── Types ───

export type BrandVariant =
  | "loaf"
  | "claude"
  | "list"
  | "bear"
  | "minimal"
  | "warm"
  | "cool"
  | "dark";

export type BrandVariantConfig = {
  chrome: Brand.ChromeVisitor;
  brandDS: Brand.DesignSystem;
  blogStyle: Option<Style.Compat>;
  blogContent: Option<Brand.BlogPage["blog"]["post"]>;
  landing: Brand.LandingPage["landing"];
  blog?: {
    index: BlogIndexRoute;
    post: BlogPostRoute;
  };
};

// ─── Instances ───

const sharedChrome = new BlogChromeImpl();

// DS class instances removed — content methods are now in blog-content.ts

const sharedPostRoute = editorialPostRoute;

// ─── Brand.DesignSystem nav factories ───

const defaultNav: Brand.DesignSystem["nav"] = {
  header: {
    make(nav) {
      const $header = MarketingHeader.make();
      // Controlled nav row: its current-page highlight is driven from outside, by
      // BrandApp.render (see BrandApp.applyNavSelection) on every navigation.
      $header.appendChild(NavCenteredPopoverRow.make().bind(nav, $header));
      // Mobile (≤768px): hamburger + full-screen overlay menu.
      $header.appendChild(MarketingMobileMenu.makeWith(nav));
      return $header;
    },
    bind($header, nav) {
      return $header;
    },
  },
  breadcrumb: {
    make() { return Breadcrumb.makeWith(); },
    bind($el) { return $el; },
  },
  footer: {
    make() {
      const $footer = LandingFooter.make();
      $footer.appendChild(MarketingFooter.make());
      return $footer;
    },
    bind($el) { return $el; },
  },
};

const bearNav: Brand.DesignSystem["nav"] = {
  header: {
    make(nav) {
      const $title = document.createElement("a");
      $title.className = "brand-nav-title brand-nav-link";
      $title.href = InstanceUrl.blog("/");
      const $titleText = document.createElement("span");
      $titleText.className = "brand-nav-title-text";
      $titleText.textContent = "Blog";
      $title.appendChild($titleText);

      const links: HTMLElement[] = [];
      for (const item of nav.items) {
        const $link = document.createElement("a");
        $link.className = "brand-nav-link";
        $link.textContent = item.label;
        $link.href = "#";
        GESTURE_MANAGER.addPointerEvent($link, {
          onClick: () => {
            if (item.href != null) nav.onNavigate(item.href);
          },
        });
        links.push($link);
      }

      return BlogBearHeader.make().bind({ title: $title, links });
    },
    bind($el) { return $el; },
  },
  breadcrumb: {
    make() {
      const $b = Breadcrumb.makeWith();
      $b.assignStyles({ marginLeft: "16px" });
      $b.append(
        Breadcrumb.Link("Home", InstanceUrl.root()),
        Breadcrumb.Link("Blog", InstanceUrl.blog()),
      );
      return $b;
    },
    bind($el) { return $el; },
  },
  footer: {
    make() {
      const $subscribe = document.createElement("p");
      $subscribe.className = "brand-footer-line brand-footer-subscribe";
      const makeLink = (label: string, href: string) => {
        const $a = document.createElement("a");
        $a.href = href;
        $a.textContent = label;
        return $a;
      };
      const $rss = makeLink("rss", "/feed/");
      $rss.classList.add("brand-footer-link");
      const $email = makeLink("email", "/subscribe/");
      $email.classList.add("brand-footer-link");
      const $hello = makeLink("hello", "/contact/");
      $hello.classList.add("brand-footer-link");
      $subscribe.append("Subscribe via ", $rss, ", ", $email, " or just say ", $hello, ".");

      const $powered = document.createElement("p");
      $powered.className = "brand-footer-line brand-footer-powered";
      $powered.textContent = "Powered by Lona.";

      return BlogBearFooter.make().bind({ lines: [$subscribe, $powered] });
    },
    bind($el) { return $el; },
  },
};

const minimalNav: Brand.DesignSystem["nav"] = {
  header: {
    make(nav) {
      const $header = MarketingHeader.make();
      $header.appendChild(NavLinksRow.make().bind(nav));
      return $header;
    },
    bind($el) { return $el; },
  },
  breadcrumb: {
    make() {
      const $b = Breadcrumb.makeWith();
      $b.assignStyles({ marginLeft: "16px" });
      $b.append(
        Breadcrumb.Link("Home", InstanceUrl.root()),
        Breadcrumb.Link("Blog", InstanceUrl.blog()),
      );
      return $b;
    },
    bind($el) { return $el; },
  },
  footer: defaultNav.footer,
};

function makeBrandDS(opts: {
  bg?: Option<string>;
  fonts?: string[];
  colors: Style.Compat;
  typography: Style.Compat;
  spacing: Style.Compat;
  contentTypography: Option<Style.Compat>;
  nav?: Brand.DesignSystem["nav"];
}): Brand.DesignSystem {
  return {
    bg: opts.bg ?? null,
    fonts: opts.fonts ?? [],
    colors: opts.colors,
    typography: opts.typography,
    contentTypography: opts.contentTypography,
    spacing: opts.spacing,
    chrome: BrandTokens.chrome,
    nav: opts.nav ?? defaultNav,
    layout: sharedChrome,
  };
}

// ─── Shared landing config ───

const sharedLanding: Brand.LandingPage["landing"] = {
  style: landingPageStyle,
  sections: defaultSections,
  page: makePageFactory(defaultSections),
};

const engineeringBlogSectionSet: SectionSet<PageEngineNodeProps> = {
  chrome: sharedChrome,
  sections: {
    "post-list": postListRenderer,
    "post-body": postBodyRenderer,
  },
};

const lonaMarketingSectionSet: SectionSet<PageEngineNodeProps> = {
  chrome: sharedChrome,
  sections: {
    hero: landingHeroRenderer,
    "feature-grid": landingFeatureGridRenderer,
    "social-proof": landingSocialProofRenderer,
    integrations: landingIntegrationsRenderer,
    comparison: landingComparisonRenderer,
    pricing: landingPricingRenderer,
    faq: landingFaqRenderer,
    cta: landingCtaRenderer,
  },
};

const loafSectionSet: SectionSet<PageEngineNodeProps> = {
  chrome: sharedChrome,
  sections: {
    ...engineeringBlogSectionSet.sections,
    ...lonaMarketingSectionSet.sections,
  },
};

// ─── Registry ───

export const BRAND_VARIANTS: Record<BrandVariant, BrandVariantConfig> = {
  loaf: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({
      bg: "#f7f3eb",
      fonts: BrandTokens.Typography.loafFonts,
      colors: BrandTokens.Color.loaf,
      typography: BrandTokens.Typography.loaf,
      spacing: BrandTokens.Spacing.loaf,
      contentTypography: null,
    }),
    blogStyle: BrandTokens.Blog.uiLoaf,
    blogContent: listContent,
    landing: sharedLanding,
    blog: {
      index: listRoute,
      post: sharedPostRoute,
    },
  },

  // Blog-first variants (have blog routes)
  claude: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({ colors: BrandTokens.Color.cream, typography: BrandTokens.Typography.none, spacing: BrandTokens.Spacing.none, contentTypography: BrandTokens.ContentTypography.editorial }),
    blogStyle: BrandTokens.Blog.ui,
    blogContent: claudeContent,
    landing: sharedLanding,
    blog: {
      index: claudeRoute,
      post: newsroomPostRoute,
    },
  },
  list: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({ colors: BrandTokens.Color.list, typography: BrandTokens.Typography.list, spacing: BrandTokens.Spacing.list, contentTypography: BrandTokens.ContentTypography.editorial }),
    blogStyle: BrandTokens.Blog.uiList,
    blogContent: listContent,
    landing: sharedLanding,
    blog: {
      index: listRoute,
      post: sharedPostRoute,
    },
  },
  bear: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({ colors: BrandTokens.Color.bear, typography: BrandTokens.Typography.bear, spacing: BrandTokens.Spacing.bear, contentTypography: BrandTokens.ContentTypography.bear, nav: bearNav }),
    blogStyle: BrandTokens.Blog.uiBear,
    blogContent: bearContent,
    landing: sharedLanding,
    blog: {
      index: bearRoute,
      post: bearPostRoute,
    },
  },
  minimal: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({ colors: BrandTokens.Color.none, typography: BrandTokens.Typography.none, spacing: BrandTokens.Spacing.minimal, contentTypography: BrandTokens.ContentTypography.editorial, nav: minimalNav }),
    blogStyle: BrandTokens.Blog.ui,
    blogContent: minimalContent,
    landing: sharedLanding,
    blog: {
      index: makeMinimalRoute({
        name: "Lona",
        bio: "Practical writing about making calendars easier to read: time zones, visual planning, stickers, sync, privacy, and the week as a surface for work and life.",
      }),
      post: minimalPostRoute,
    },
  },

  // Landing-first variants (no blog routes — blog falls back to loaf)
  warm: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({ bg: "#f5f0e8", fonts: BrandTokens.Typography.sansFonts, colors: BrandTokens.Color.warm, typography: BrandTokens.Typography.sans, spacing: BrandTokens.Spacing.comfortable, contentTypography: null }),
    blogStyle: null,
    blogContent: null,
    landing: sharedLanding,
  },
  cool: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({ bg: "#ffffff", fonts: BrandTokens.Typography.sansFonts, colors: BrandTokens.Color.cool, typography: BrandTokens.Typography.sans, spacing: BrandTokens.Spacing.compact, contentTypography: null }),
    blogStyle: null,
    blogContent: null,
    landing: sharedLanding,
  },
  dark: {
    chrome: sharedChrome,
    brandDS: makeBrandDS({ bg: "#1a1a1a", fonts: BrandTokens.Typography.sansFonts, colors: BrandTokens.Color.dark, typography: BrandTokens.Typography.sans, spacing: BrandTokens.Spacing.comfortable, contentTypography: null }),
    blogStyle: null,
    blogContent: null,
    landing: sharedLanding,
  },
};

export const SECTION_SETS: Record<PageEnginePresetName, SectionSet<PageEngineNodeProps>> = {
  "engineering-blog": engineeringBlogSectionSet,
  "lona-marketing": lonaMarketingSectionSet,
  loaf: loafSectionSet,
  tsh: tshSectionSet,
};

export const TOKEN_SETS: Record<PageEnginePresetName, TokenSet> = {
  "engineering-blog": loafTokenSet(),
  "lona-marketing": loafTokenSet(),
  loaf: loafTokenSet(),
  // tsh brings ONLY its own tokens — no loaf ramp underneath. The whole design
  // is one size in one face, and inheriting a five-step scale it then has to
  // override at every role is how a port ends up fighting its own base.
  tsh: tshTokens,
};

// Section overrides for marketing pages: the `landing.stickers` node is a
// generic `feature-grid` for SSR, but renders as a bespoke sticker bento on the
// client (see sticker-section.ts and escape-hatches.md).
const landingOverrides: RendererOverrides<PageEngineNodeProps> = {
  byId: { "landing.stickers": stickerSheetRenderer },
  byKind: null,
};

export const PRESETS: Record<PageEnginePresetName, Preset<PageEngineNodeProps>> = {
  "engineering-blog": {
    name: "engineering-blog",
    theme: {
      tokens: "engineering-blog",
      variant: "engineering-blog",
    },
    overrides: null,
  },
  "lona-marketing": {
    name: "lona-marketing",
    theme: {
      tokens: "lona-marketing",
      variant: "lona-marketing",
    },
    overrides: landingOverrides,
  },
  loaf: {
    name: "loaf",
    theme: {
      tokens: "loaf",
      variant: "loaf",
    },
    overrides: landingOverrides,
  },
  tsh: {
    name: "tsh",
    theme: {
      tokens: "tsh",
      variant: "tsh",
    },
    // No sticker override: that one exists to turn a marketing feature-grid
    // into a bento, and tsh's feature-grid is a list.
    overrides: null,
  },
};

export function resolvePageEnginePreset(
  theme: ThemeRef,
): PageEnginePresetConfig {
  const name = resolvePageEnginePresetName(theme);
  return {
    preset: PRESETS[name],
    sectionSet: SECTION_SETS[name],
    tokenSet: TOKEN_SETS[name],
  };
}

function loafTokenSet(): TokenSet {
  const config = BRAND_VARIANTS.loaf;
  const tokens = Brand.collectLandingTokens({
    ds: config.brandDS,
    landing: config.landing,
  });
  if (config.blogStyle) tokens.push(config.blogStyle);
  return tokens;
}

// ─── Variant resolution ───

const STORAGE_KEY = "brand-variant";
const DEFAULT_VARIANT: BrandVariant = "loaf";
const ALL_VARIANTS: BrandVariant[] = [
  "loaf",
  "claude", "list", "bear", "minimal",
  "warm", "cool", "dark",
];

export const showVariantPicker = new Flags.Debug("brand-show-variant-picker", false);

export function resolveVariant(): BrandVariant {
  const urlParams = new URLSearchParams(window.location.search);
  const rawUrlVariant = urlParams.get("variant");
  const rawStoredVariant = localStorage.getItem(STORAGE_KEY);
  const urlVariant =
    rawUrlVariant != null && isBrandVariant(rawUrlVariant) ? rawUrlVariant : null;
  const storedVariant =
    rawStoredVariant != null && isBrandVariant(rawStoredVariant)
      ? rawStoredVariant
      : null;
  const variant = urlVariant ?? storedVariant ?? DEFAULT_VARIANT;

  if (variant !== storedVariant) {
    localStorage.setItem(STORAGE_KEY, variant);
  }

  return variant;
}

export function allVariants(): BrandVariant[] {
  return ALL_VARIANTS;
}

export function isBrandVariant(value: string): value is BrandVariant {
  for (const variant of ALL_VARIANTS) {
    if (variant === value) return true;
  }
  return false;
}

export function switchVariant(variant: BrandVariant) {
  localStorage.setItem(STORAGE_KEY, variant);
  const url = new URL(window.location.href);
  url.searchParams.set("variant", variant);
  window.location.href = url.toString();
}
